import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, User, Mail, Phone, Users, Check, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to parse dates safely
const parseDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

export const BookingFormInline = ({ extractedData, onClose, onSuccess }) => {
  const { t, language } = useLanguage();
  const [step, setStep] = useState(1); // 1: personal info, 2: dates & room
  const [loading, setLoading] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [upsells, setUpsells] = useState([]);
  const [selectedUpsells, setSelectedUpsells] = useState([]);

  const [formData, setFormData] = useState({
    fullName: extractedData?.name || '',
    email: extractedData?.email || '',
    phone: extractedData?.phone || '',
    checkIn: parseDate(extractedData?.check_in_date),
    checkOut: parseDate(extractedData?.check_out_date),
    guests: parseInt(extractedData?.guests) || 2,
    roomId: extractedData?.room_id || '',  // Changed from roomType to roomId
    specialRequests: ''
  });

  const [errors, setErrors] = useState({});

  // Fetch available rooms and upsells
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        console.log('📍 Fetching rooms from:', `${API}/rooms`);
        const response = await axios.get(`${API}/rooms`);
        console.log('✅ Rooms loaded:', response.data);
        setRooms(response.data);
        
        // Fetch upsells
        const upsellsResponse = await axios.get(`${API}/upsells`);
        console.log('✅ Upsells loaded:', upsellsResponse.data);
        setUpsells(upsellsResponse.data);
      } catch (error) {
        console.error('❌ Error fetching rooms:', error);
        toast.error('Failed to load rooms. Please refresh.');
      }
    };
    fetchRooms();
  }, []);

  // Debug and validate extracted data
  useEffect(() => {
    if (extractedData && rooms.length > 0) {
      console.log('📋 Extracted Data:', extractedData);
      console.log('📅 Parsed Dates:', {
        checkIn: parseDate(extractedData?.check_in_date),
        checkOut: parseDate(extractedData?.check_out_date)
      });
      
      // Validate and fix room_id if needed
      if (extractedData.room_id) {
        const roomExists = rooms.find(r => r.id === extractedData.room_id);
        if (!roomExists) {
          console.warn('⚠️ Invalid room_id in extracted data:', extractedData.room_id);
          console.log('📋 Available room IDs:', rooms.map(r => r.id));
          // Try to match by name
          const matchedRoom = rooms.find(r => 
            Object.values(r.name || {}).some(name => 
              name.toLowerCase().includes(extractedData.room_id.toLowerCase()) ||
              extractedData.room_id.toLowerCase().includes(name.toLowerCase())
            )
          );
          if (matchedRoom) {
            console.log('✅ Matched room by name:', matchedRoom.id);
            setFormData(prev => ({ ...prev, roomId: matchedRoom.id }));
          } else {
            console.log('❌ Could not match room, clearing room_id');
            setFormData(prev => ({ ...prev, roomId: '' }));
          }
        } else {
          console.log('✅ Valid room_id:', extractedData.room_id);
        }
      }
    }
  }, [extractedData, rooms]);

  // Validation
  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^[\d\s\+\-\(\)]+$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone format';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    
    if (!formData.checkIn) {
      newErrors.checkIn = 'Check-in date is required';
    }
    
    if (!formData.checkOut) {
      newErrors.checkOut = 'Check-out date is required';
    }
    
    if (formData.checkIn && formData.checkOut) {
      const checkIn = new Date(formData.checkIn);
      const checkOut = new Date(formData.checkOut);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (checkIn < today) {
        newErrors.checkIn = 'Check-in date cannot be in the past';
      }
      
      if (checkOut <= checkIn) {
        newErrors.checkOut = 'Check-out must be after check-in';
      }
    }
    
    if (!formData.roomId) {
      newErrors.roomId = 'Please select a room type';
    } else if (rooms.length > 0) {
      // Validate that the roomId exists in available rooms
      const roomExists = rooms.find(r => r.id === formData.roomId);
      if (!roomExists) {
        newErrors.roomId = 'Invalid room selection. Please select again.';
        console.error('❌ Room validation failed:', formData.roomId, 'Available:', rooms.map(r => r.id));
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleNext = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep2()) {
      return;
    }

    setLoading(true);
    try {
      // Find selected room by ID
      const selectedRoom = rooms.find(r => r.id === formData.roomId);
      
      console.log('🔍 Looking for room:', formData.roomId);
      console.log('📋 Available rooms:', rooms.map(r => ({ id: r.id, name: r.name })));
      console.log('✅ Selected room:', selectedRoom);
      
      if (!selectedRoom) {
        console.error('❌ Room not found! roomId:', formData.roomId);
        console.error('❌ Available IDs:', rooms.map(r => r.id).join(', '));
        toast.error(`Selected room not found. Please select a valid room from the list.`);
        setLoading(false);
        return;
      }

      // Prepare booking data
      const bookingData = {
        room_id: selectedRoom.id,
        guest_name: formData.fullName,
        guest_email: formData.email,
        guest_phone: formData.phone,
        check_in: new Date(formData.checkIn).toISOString(),
        check_out: new Date(formData.checkOut).toISOString(),
        guests: parseInt(formData.guests),
        upsell_ids: selectedUpsells,
        special_requests: formData.specialRequests,
        language: language
      };

      console.log('📤 Sending booking data:', bookingData);
      console.log('📤 Data types:', {
        room_id: typeof bookingData.room_id,
        guest_name: typeof bookingData.guest_name,
        guest_email: typeof bookingData.guest_email,
        guest_phone: typeof bookingData.guest_phone,
        check_in: typeof bookingData.check_in,
        check_out: typeof bookingData.check_out,
        guests: typeof bookingData.guests,
        upsell_ids: typeof bookingData.upsell_ids,
        special_requests: typeof bookingData.special_requests,
        language: typeof bookingData.language
      });

      // Create booking
      const bookingResponse = await axios.post(`${API}/bookings`, bookingData);

      const bookingId = bookingResponse.data.id;

      // Create checkout session
      const checkoutResponse = await axios.post(`${API}/payments/checkout`, {
        booking_id: bookingId,
        origin_url: window.location.origin
      });

      // Redirect to payment
      if (checkoutResponse.data.url) {
        window.location.href = checkoutResponse.data.url;
      }

      onSuccess && onSuccess(bookingResponse.data);
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  // Calculate nights
  const nights = formData.checkIn && formData.checkOut
    ? Math.ceil((new Date(formData.checkOut) - new Date(formData.checkIn)) / (1000 * 60 * 60 * 24))
    : 0;

  // Calculate prices
  const selectedRoom = rooms.find(r => r.id === formData.roomId);
  const roomPrice = selectedRoom && nights > 0 ? selectedRoom.price_per_night * nights : 0;
  
  // Calculate extra guests price
  const baseGuests = selectedRoom?.base_guests || 2;
  const extraGuestPrice = selectedRoom?.extra_guest_price_per_night || 20;
  const extraGuests = Math.max(0, formData.guests - baseGuests);
  const extraGuestsPrice = extraGuests > 0 ? extraGuests * extraGuestPrice * nights : 0;
  
  const upsellsPrice = selectedUpsells.reduce((sum, upsellId) => {
    const upsell = upsells.find(u => u.id === upsellId);
    return sum + (upsell ? upsell.price : 0);
  }, 0);
  const totalPrice = roomPrice + extraGuestsPrice + upsellsPrice;

  // Toggle upsell selection
  const toggleUpsell = (upsellId) => {
    setSelectedUpsells(prev => 
      prev.includes(upsellId)
        ? prev.filter(id => id !== upsellId)
        : [...prev, upsellId]
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="bg-white rounded-lg shadow-xl p-4 sm:p-6 w-full max-w-md mx-auto my-2"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-[#0F1F3C]">
          🏨 {step === 1 ? 'Your Information' : 'Booking Details'}
        </h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Progress Bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            step >= 1 ? 'bg-[#D4AF37] text-white' : 'bg-gray-200 text-gray-500'
          }`}>
            1
          </div>
          <span className="text-sm font-medium">Info</span>
        </div>
        <div className="flex-1 h-1 bg-gray-200 mx-3">
          <div 
            className="h-full bg-[#D4AF37] transition-all duration-300"
            style={{ width: step === 2 ? '100%' : '0%' }}
          />
        </div>
        <div className="flex items-center space-x-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            step >= 2 ? 'bg-[#D4AF37] text-white' : 'bg-gray-200 text-gray-500'
          }`}>
            2
          </div>
          <span className="text-sm font-medium">Booking</span>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {step === 1 ? (
          /* Step 1: Personal Information */
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <User className="w-4 h-4 inline mr-1" />
                Full Name *
              </label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                  errors.fullName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="John Doe"
              />
              {errors.fullName && (
                <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Mail className="w-4 h-4 inline mr-1" />
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="john@example.com"
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Phone className="w-4 h-4 inline mr-1" />
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="+212 600000000"
              />
              {errors.phone && (
                <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
              )}
            </div>

            <button
              type="button"
              onClick={handleNext}
              className="w-full bg-[#D4AF37] text-white py-3 rounded-lg font-medium hover:bg-[#B5952F] transition-colors"
            >
              Next Step →
            </button>
          </div>
        ) : (
          /* Step 2: Booking Details */
          <div className="space-y-4">
            {/* Check-in Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Check-in Date *
              </label>
              <input
                type="date"
                name="checkIn"
                value={formData.checkIn}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                  errors.checkIn ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.checkIn && (
                <p className="text-red-500 text-xs mt-1">{errors.checkIn}</p>
              )}
            </div>

            {/* Check-out Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Check-out Date *
              </label>
              <input
                type="date"
                name="checkOut"
                value={formData.checkOut}
                onChange={handleChange}
                min={formData.checkIn || new Date().toISOString().split('T')[0]}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                  errors.checkOut ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.checkOut && (
                <p className="text-red-500 text-xs mt-1">{errors.checkOut}</p>
              )}
            </div>

            {/* Number of Guests */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Users className="w-4 h-4 inline mr-1" />
                Number of Guests
              </label>
              <select
                name="guests"
                value={formData.guests}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
              >
                {[1, 2, 3, 4, 5, 6].map(num => (
                  <option key={num} value={num}>{num} {num === 1 ? 'Guest' : 'Guests'}</option>
                ))}
              </select>
              {selectedRoom && extraGuests > 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  ℹ️ +€{extraGuestPrice}/night for each extra guest beyond {baseGuests}
                </p>
              )}
            </div>

            {/* Room Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                🛏️ Room Type *
              </label>
              <select
                name="roomId"
                value={formData.roomId}
                onChange={handleChange}
                disabled={rooms.length === 0}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] disabled:bg-gray-100 disabled:cursor-not-allowed ${
                  errors.roomId ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">{rooms.length === 0 ? 'Loading rooms...' : 'Select a room'}</option>
                {rooms.map(room => (
                  <option key={room.id} value={room.id}>
                    {room.name?.en || room.name?.fr || room.name?.ar || 'Room'} - €{room.price_per_night}/night
                  </option>
                ))}
              </select>
              {errors.roomId && (
                <p className="text-red-500 text-xs mt-1">{errors.roomId}</p>
              )}
            </div>

            {/* Special Requests */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Special Requests (Optional)
              </label>
              <textarea
                name="specialRequests"
                value={formData.specialRequests}
                onChange={handleChange}
                rows="2"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] resize-none"
                placeholder="Any special requirements?"
              />
            </div>

            {/* Additional Services */}
            {upsells.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ✨ Additional Services (Optional)
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {upsells.map(upsell => (
                    <label
                      key={upsell.id}
                      className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-all ${
                        selectedUpsells.includes(upsell.id)
                          ? 'border-[#D4AF37] bg-[#FFF9E6]'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={selectedUpsells.includes(upsell.id)}
                          onChange={() => toggleUpsell(upsell.id)}
                          className="w-4 h-4 text-[#D4AF37] border-gray-300 rounded focus:ring-[#D4AF37]"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-800">
                            {upsell.name?.en || upsell.name?.fr || upsell.name?.ar || 'Service'}
                          </p>
                          {upsell.duration && (
                            <p className="text-xs text-gray-500">{upsell.duration}</p>
                          )}
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-[#D4AF37]">
                        €{upsell.price}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Price Summary */}
            {nights > 0 && roomPrice > 0 && (
              <div className="bg-[#F9F8F6] rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Room ({nights} night{nights > 1 ? 's' : ''})</span>
                  <span className="font-medium">€{selectedRoom.price_per_night} × {nights} = €{roomPrice}</span>
                </div>
                {extraGuestsPrice > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Extra guests ({extraGuests})</span>
                    <span className="font-medium">€{extraGuestPrice} × {extraGuests} × {nights} = €{extraGuestsPrice}</span>
                  </div>
                )}
                {upsellsPrice > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Additional Services</span>
                    <span className="font-medium">€{upsellsPrice}</span>
                  </div>
                )}
                <div className="border-t pt-2 flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span className="text-[#D4AF37]">€{totalPrice}</span>
                </div>
              </div>
            )}

            {/* Buttons */}
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              >
                ← Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[#D4AF37] text-white py-3 rounded-lg font-medium hover:bg-[#B5952F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Processing...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Continue to Payment
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </form>
    </motion.div>
  );
};
